import React, { useEffect, useRef, useMemo } from "react";
import ClientSuccess from "../components/ClientSuccess";
import { Link } from "react-router-dom";
import "./About.css";

const Timeline = () => {
  const timelineData = useMemo(
    () => [
      {
        year: "2019",
        title: "The Foundation of Integrity",
        description:
          "Alpha Accountants Inc. was born from a singular mission: to redefine the accounting experience. Founded on the principles of integrity and expertise, we opened our doors to provide a transparent, client-first alternative in a crowded financial marketplace.",
        image:
          "https://res.cloudinary.com/do35rm84a/image/upload/v1757944772/2019_tt6jg3.avif",
      },
      {
        year: "2020",
        title: "Resilience Amidst Complexity",
        description:
          "As the global landscape shifted, our dedication to simplifying the financial landscape was put to the ultimate test. We pivoted to help businesses navigate unprecedented economic hurdles, proving that our commitment goes beyond numbers by prioritizing the survival and stability of our partners.",
        image:
          "https://res.cloudinary.com/do35rm84a/image/upload/v1757944773/2020_lvhlih.avif",
      },
      {
        year: "2021",
        title: "Expanding the Expertise",
        description:
          "With a growing reputation for excellence, we scaled our team of specialists. We didn't just track wealth; we began fostering growth through proactive tax planning and advisory services, ensuring our clients stayed ahead of compliance requirements in a rapidly changing US economy.",
        image:
          "https://res.cloudinary.com/do35rm84a/image/upload/v1757944770/2021_os6jfi.avif",
      },
      {
        year: "2022",
        title: "Cultivating Lasting Relationships",
        description:
          "This year marked our transition from a service provider to a dedicated partner. We focused on building lasting relationships based on trust, realizing that our success was inextricably linked to the mutual success of the entrepreneurs and individuals we served.",
        image:
          "https://res.cloudinary.com/do35rm84a/image/upload/v1757944768/2022_naf7dy.avif",
      },
      {
        year: "2024",
        title: "Digital Innovation & Precision",
        description:
          "After a period of internal refinement, we integrated cutting-edge financial technologies to further simplify the complexities of the US financial landscape. By merging modern tools with human expertise, we provided our clients with real-time clarity and even greater strategic depth.",
        image:
          "https://res.cloudinary.com/do35rm84a/image/upload/v1757944769/2023_k8auio.avif",
      },
      {
        year: "2025",
        title: "The Vision Realized",
        description:
          "Today, Alpha Accountants Inc. stands as a beacon of exceptional accounting and advisory services. As we look to the future, our approach remains unchanged: placing the client at the center of every balance sheet and ensuring that every financial decision leads toward a legacy of growth.",
        image:
          "https://res.cloudinary.com/do35rm84a/image/upload/v1757944769/2025_gaot9j.avif",
      },
    ],
    [],
  );

  const progressRef = useRef(null);
  const itemsRef = useRef([]);
  const dotsRef = useRef([]);

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, timelineData.length);
    dotsRef.current = dotsRef.current.slice(0, timelineData.length);
  }, [timelineData]);

  useEffect(() => {
    const handleScroll = () => {
      const firstItem = itemsRef.current[0];
      const lastItem = itemsRef.current[itemsRef.current.length - 1];
      const progressEl = progressRef.current;
      if (!firstItem || !lastItem || !progressEl) return;

      const screenCenter = window.innerHeight / 2;
      const firstItemRect = firstItem.getBoundingClientRect();
      const firstItemTop = firstItemRect.top;

      const progressHeight = screenCenter - firstItemTop;
      const totalHeight =
        lastItem.offsetTop + lastItem.offsetHeight - firstItem.offsetTop;

      const clampedHeight = Math.max(0, Math.min(progressHeight, totalHeight));
      progressEl.style.height = `${clampedHeight}px`;

      itemsRef.current.forEach((item, index) => {
        const dot = dotsRef.current[index];
        if (!item || !dot) return;

        const itemRect = item.getBoundingClientRect();
        if (itemRect.top < screenCenter) {
          item.classList.add("is-scrolled-past");
          dot.classList.add("visible");
        } else {
          item.classList.remove("is-scrolled-past");
          dot.classList.remove("visible");
        }
      });
    };

    const setupTimeline = () => {
      const firstItem = itemsRef.current[0];
      const progressEl = progressRef.current;
      if (!firstItem || !progressEl) return;

      const startY = firstItem.offsetTop;
      progressEl.style.top = `${startY}px`;
      handleScroll();
    };

    setupTimeline();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", setupTimeline, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", setupTimeline);
    };
  }, [timelineData]);

  return (
    <section className="timeline-section">
      <div className="container">
        <div className="section-header">
          <h2 className="font-serif text-4xl lg:text-5xl text-[#203468] mb-4">
            Our Journey
          </h2>
          <p className="text-slate-600 font-light">
            Tracing the milestones that have shaped our story of growth and
            innovation.
          </p>
        </div>
        <div className="timeline-container">
          <div className="timeline-axis"></div>
          <div className="timeline-progress-bar" ref={progressRef}></div>
          <div className="timeline-items-wrapper">
            {timelineData.map((item, index) => (
              <div
                className="timeline-item"
                key={index}
                ref={(el) => (itemsRef.current[index] = el)}
              >
                <div
                  className="timeline-dot"
                  ref={(el) => (dotsRef.current[index] = el)}
                ></div>
                <div className="timeline-content">
                  <div className="timeline-image">
                    <img src={item.image} alt={item.title} loading="lazy" />
                  </div>
                  <div className="timeline-text">
                    <span className="timeline-year">{item.year}</span>
                    <h3 className="timeline-title">{item.title}</h3>
                    <p className="timeline-description">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const About = () => {
  return (
    <>
      <div className="about-page bg-white">
        {/* Editorial Header Section */}
        <div className="bg-white pt-28">
          <div className="relative overflow-hidden bg-[#203468] py-16 lg:py-24 text-white">
            {/* 🔷 Animated grid background */}
            <div className="absolute inset-0 grid-bg opacity-20"></div>

            {/* 🔵 Moving gradient beam */}
            <div className="absolute inset-0">
              <div className="gradient-beam"></div>
            </div>

            {/* 🟣 Floating particles */}
            <div className="absolute inset-0 pointer-events-none">
              <span className="particle w-2 h-2 left-[10%] top-[20%]"></span>
              <span className="particle w-3 h-3 left-[70%] top-[40%]"></span>
              <span className="particle w-2 h-2 left-[50%] top-[70%]"></span>
              <span className="particle w-3 h-3 left-[30%] top-[80%]"></span>
            </div>

            {/* 🧊 Glass layer */}
            <div className="absolute inset-0 backdrop-blur-md bg-white/5"></div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl">
                {/* 🔥 Animated heading */}
                <h1 className="font-serif text-5xl lg:text-7xl leading-tight mb-8">
                  <span className="word">About</span>{" "}
                  <span className="word text-blue-400 italic">The Alpha CPAs</span>
                </h1>

                {/* ✨ Subtitle */}
                <p className="text-slate-300 text-xl leading-relaxed max-w-2xl fade-up delay-2">
                  A legacy of trust, precision, and financial excellence since
                  2019. We build the foundation for your future.
                </p>

                {/* 🚀 CTA */}
                <div className="mt-8 fade-up delay-3">
                  <div className="mt-6 fade-up delay-3">
                    <Link
                      to="/team"
                      className="relative inline-block px-8 py-3 rounded-full bg-blue-500 border border-white text-white overflow-hidden group"
                    >
                      <span className="relative z-10">Meet Our Leadership</span>

                      <span className="absolute inset-0 bg-blue-400 opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></span>
                      <span className="shine"></span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content - Split Editorial Layout */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
              {/* Left Column - Sticky Image */}
              <div className="lg:col-span-5 relative">
                <div className="sticky top-32">
                  <div className="relative">
                    <img
                      src="/images/photos/about-partners-meeting.webp"
                      alt="Partners meeting"
                      className="w-full h-[600px] object-cover grayscale hover:grayscale-0 transition-all duration-700"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute -bottom-10 -right-10 bg-[#019BD7] text-white p-10 hidden md:block shadow-2xl">
                      <p className="text-6xl font-serif font-bold mb-2">
                        3<span className="text-blue-300">+</span>
                      </p>
                      <p className="text-xs font-bold uppercase tracking-[0.2em]">
                        Years of
                        <br />
                        Excellence
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Typography Heavy */}
              <div className="lg:col-span-7 lg:pl-10">
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#019BD7] mb-6">
                  The Firm
                </h2>
                <h3 className="font-serif text-4xl lg:text-5xl text-[#203468] mb-10 leading-tight">
                  The Vision Behind Alpha <span className="text-orange-500">Accountants</span> Inc.
                </h3>

                <div className="space-y-8 text-slate-600 text-lg leading-relaxed font-light mb-16">
                  <p className="text-xl text-[#203468] font-medium">
                    Alpha Accountants Inc. was founded with a clear mission: to
                    provide exceptional accounting, tax, and advisory services
                    with integrity, expertise, and a client-first approach. We
                    understand the complexities of the US financial landscape
                    and are dedicated to simplifying it for our clients,
                    fostering growth and ensuring compliance. Our commitment
                    goes beyond numbers; we aim to build lasting relationships
                    based on trust and mutual success.
                  </p>
                  <p>
                    Founded by a team of forward-thinking financial
                    professionals, the firm was built to address the critical
                    capacity challenges facing CPA practices worldwide. We
                    recognized that for firms to truly lead, they needed more
                    than just "extra hands" they needed an elite, high-precision
                    engine capable of handling technical complexity at scale.
                  </p>
                  {/* <p>
                    Though our history is young, our impact is significant.
                    Today, The Alpha CPAs stands as a premier outsourcing
                    destination for firms that refuse to compromise on accuracy.
                    We continue to invest in elite talent and emerging financial
                    technologies, ensuring that the "Alpha Standard" remains the
                    benchmark for the industry.
                  </p> */}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 border-t border-slate-200 pt-16">
                  <div>
                    <h4 className="font-serif text-2xl text-[#203468] mb-4">
                      Precision
                    </h4>
                    <p className="text-slate-600 font-light leading-relaxed">
                      To A "zero-error" culture established at our founding.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-serif text-2xl text-[#203468] mb-4">
                      Security
                    </h4>
                    <p className="text-slate-600 font-light leading-relaxed">
                      Investing in enterprise-grade data protection from year
                      one.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr className="border-slate-100" />

        {/* Timeline Section */}
        <Timeline />

        <ClientSuccess />
      </div>
    </>
  );
};

export default About;
